import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getSalesData, setAnalyticsDateRange } from '../../redux/actions/analyticsAction';
import FYSelector from '../../components/utility/FYSelector';
import Loader from '../../utility/Loader';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

const SalesAnalytics = () => {
    const dispatch = useDispatch();
    const { salesData, loading, startDate, endDate, financialYear } = useSelector(state => state.analytics);

    useEffect(() => {
        dispatch(getSalesData(startDate, endDate));
    }, [dispatch, financialYear]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalSales = salesData?.reduce((acc, curr) => acc + curr.totalAmount, 0) || 0;
    const totalBills = salesData?.reduce((acc, curr) => acc + curr.billCount, 0) || 0;

    const handleDateChange = (field, value) => {
        const newStart = field === 'start' ? value : startDate;
        const newEnd = field === 'end' ? value : endDate;
        dispatch(setAnalyticsDateRange(newStart, newEnd));
    };

    const handleApply = () => {
        dispatch(getSalesData(startDate, endDate));
    };


    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '12px 16px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                    <p className="mb-1 fw-semibold">{moment(label).format('DD MMM YYYY')}</p>
                    <p className="mb-0 text-primary">₹{payload[0].value.toLocaleString('en-IN')}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <section className="py-5">
            <div className="container">
                <div className="mb-4">
                    <h2 className="text-warning fw-bold mb-1">Sales Statistics</h2>
                    <p className="text-muted mb-0">Daily sales trend · Tax Invoice {financialYear.slice(0,2)}-{financialYear.slice(2)} only</p>
                </div>

                <FYSelector />

                {/* Date Range */}
                <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '12px' }}>
                    <div className="row g-3 align-items-end">
                        <div className="col-auto">
                            <label className="form-label small text-muted fw-semibold mb-1">START DATE</label>
                            <input type="date" className="form-control" value={startDate} onChange={e => handleDateChange('start', e.target.value)} />
                        </div>
                        <div className="col-auto">
                            <label className="form-label small text-muted fw-semibold mb-1">END DATE</label>
                            <input type="date" className="form-control" value={endDate} onChange={e => handleDateChange('end', e.target.value)} />
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary btn-sm" onClick={handleApply}>
                                Apply
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? <Loader /> : (
                    <>
                        {/* Summary row */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px' }}>
                                    <p className="text-muted small mb-1">Total Revenue</p>
                                    <h4 className="fw-bold text-primary mb-0">₹{totalSales.toLocaleString('en-IN')}</h4>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px' }}>
                                    <p className="text-muted small mb-1">Total Bills</p>
                                    <h4 className="fw-bold text-success mb-0">{totalBills}</h4>
                                </div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '15px' }}>
                            {salesData && salesData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={380}>
                                    <AreaChart data={salesData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.15} />
                                                <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={str => moment(str).format('DD MMM')}
                                            stroke="#aaa"
                                            fontSize={11}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            stroke="#aaa"
                                            fontSize={11}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={val => val >= 1000 ? `₹${(val / 1000).toFixed(0)}k` : `₹${val}`}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area
                                            type="monotone"
                                            dataKey="totalAmount"
                                            stroke="#0d6efd"
                                            strokeWidth={2.5}
                                            fill="url(#salesGradient)"
                                            dot={{ r: 3, fill: '#0d6efd', strokeWidth: 0 }}
                                            activeDot={{ r: 5 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    No sales data found for the selected date range
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default SalesAnalytics;
